import { AxiosInstance } from 'axios';
import { DataResponse, PayloadRequest } from '../store/services/requestScreen/TypesRequestScreen';
import {
  ChooseAddonsPayload,
  ChooseAddonsResponse,
  ChooseDirectorsPayload,
  ChooseObjectivesPayload,
  ChooseObjectivesResponse,
  ChooseShareholdersPayload,
  EnumDocumentType,
  GetAllJurisdictionsResponse,
  GetCompanyDocumentsResponse,
  GetCompanyDocumentsUserResponse,
  GetFeesResponse,
  GetFeesResponsePaginated,
  GetJurisdictionPayload,
  GetJurisdictionResponse,
  GetJurisdictionsCompanyStructuresResponse,
  GetJurisdictionsResponse,
  GetSummaryPayload,
  GetSummaryResponse,
  IAccountPlan,
  ILanguagePersistence,
  IUserContract,
  PaymentStatus,
  ServiceBaseConfig,
  UpdateFeePayload,
  UpdateJurisdictionPayload
} from './Interfaces/IAccountService';

export class AccountService {
  public static async chooseObjectives(
    api: AxiosInstance,
    payload: ChooseObjectivesPayload,
    config: ServiceBaseConfig
  ): Promise<ChooseObjectivesResponse> {
    const { data } = await api.post('/plan/choose-objectives', payload, config);

    return data;
  }

  public static async chooseDirectors(
    api: AxiosInstance,
    payload: ChooseDirectorsPayload,
    config: ServiceBaseConfig
  ): Promise<ChooseObjectivesResponse> {
    const { data } = await api.post('/plan/directors', payload, config);

    return data;
  }

  public static async chooseShareholders(
    api: AxiosInstance,
    payload: ChooseShareholdersPayload,
    config: ServiceBaseConfig
  ): Promise<ChooseObjectivesResponse> {
    const { data } = await api.post('/plan/shareholders', { ...payload, underages: 0 }, config);

    return data;
  }

  public static async chooseAddons(
    api: AxiosInstance,
    payload: ChooseAddonsPayload,
    config: ServiceBaseConfig
  ): Promise<ChooseAddonsResponse> {
    const { data } = await api.post('/plan/addons', payload, config);

    return data;
  }

  public static async getLegalPersonality(
    api: AxiosInstance,
    planSlug: string,
    config: ServiceBaseConfig
  ): Promise<ChooseObjectivesResponse> {
    const { data } = await api.get('/plan/get-legal-personality', {
      params: {
        slug_plan: planSlug
      },
      ...config
    });

    return data;
  }

  public static async getJurisdiction(
    api: AxiosInstance,
    payload: GetJurisdictionPayload,
    config: ServiceBaseConfig
  ): Promise<GetJurisdictionResponse> {
    const { data } = await api.post('/plan/jurisdictions', payload, config);

    return data;
  }

  public static async getJurisdictions(
    api: AxiosInstance,
    config: ServiceBaseConfig
  ): Promise<GetJurisdictionsResponse> {
    const { data } = await api.get('/jurisdictions', config);

    return data;
  }

  public static async getStructureBasedJurisdiction(
    jurisdictionId: number,
    api: AxiosInstance,
    config: ServiceBaseConfig,
  ): Promise<GetJurisdictionsCompanyStructuresResponse> {
    const { data } = await api.get(`/jurisdictions/${jurisdictionId}/company-structures`, config);

    return data;
  }

  public static async getSummary(
    api: AxiosInstance,
    payload: GetSummaryPayload,
    config: ServiceBaseConfig
  ): Promise<GetSummaryResponse> {
    const { data } = await api.post('/plan/summary', payload, config);

    return data;
  }

  

  


  public static async getCompanyDocuments(
    api: AxiosInstance,
    companySlug: string | number,
    config: ServiceBaseConfig
  ): Promise<GetCompanyDocumentsResponse> {
    const { data } = await api.get(`/companies/${companySlug}/documents`, config);

    return data;
  }

  public static async getCheckoutStatus(
    api: AxiosInstance,
    payload: GetSummaryPayload,
    config: ServiceBaseConfig
  ): Promise<{
    data: {
      company_type: string;
      status: PaymentStatus;
      success: boolean;
    };
  }> {
    const { data } = await api.post('/checkout/paymentSucceeded', payload, config);

    return data;
  }

  public static async getAccountPlans(api: AxiosInstance, config: ServiceBaseConfig): Promise<IAccountPlan> {
    const { data } = await api.get('/users/accounts-plans', config);

    return data;
  }

  public static async getUserContract(
    api: AxiosInstance,
    query: string,
    config: ServiceBaseConfig
  ): Promise<[IUserContract]> {
    const { data } = await api.get(`/users/me/documents?${query}`, config);

    return data;
  }

  public static async getCompaniesDocuments(
    api: AxiosInstance,
    companyId: string | number,
    typeDocuments: EnumDocumentType,
    config: ServiceBaseConfig
  ): Promise<GetCompanyDocumentsUserResponse> {
    const { data } = await api.get(`users/me/companies/${companyId}/documents?type=${typeDocuments}`, config);

    return data;
  }
  public static async updatedLanguageState(
    api: AxiosInstance,
    preferredLanguage: ILanguagePersistence,
    config: ServiceBaseConfig
  ): Promise<any> {
    const { data } = await api.put('/users/change-language', preferredLanguage, config);

    return data;
  }

  public static async getDownloadDocuments(
    api: AxiosInstance,
    documentId: string | number,
    config: ServiceBaseConfig
  ): Promise<any> {
    const { data } = await api.get(`companies/documents/download/${documentId}`, config);

    return data;
  }

  public static async getPlan(api: AxiosInstance, slug: string | undefined, config: ServiceBaseConfig): Promise<any> {
    const { data } = await api.get(`/plan/${slug}/configuration`, config);

    return data;
  }

  public static async getRequests(
    api: AxiosInstance,
    companyId: number | undefined,
    config: ServiceBaseConfig
  ): Promise<DataResponse> {
    const { data } = await api.get(`companies/services-requests?companyId=${companyId}`, config);

    return data;
  }

  public static async addRequests(
    api: AxiosInstance,
    payload: PayloadRequest,
    config: ServiceBaseConfig
  ): Promise<any> {
    const { data } = await api.post('companies/services-requests', payload, config);

    return data;
  }

  public static async getForOnGoingRequest(
    api: AxiosInstance,
    companyId: number | undefined,
    page: number,
    config: ServiceBaseConfig
  ): Promise<any> {
    const { data } = await api.get(`companies/${companyId}/services-requests?page=${page}`, config);

    return data;
  }

  public static async changeCompanySelected(
    api: AxiosInstance,
    companyId: { company_id: number } | undefined,
    config: ServiceBaseConfig
  ): Promise<any> {
    const { data } = await api.post('/change-company', companyId, config);

    return data;
  }
}
